import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Button, TextField, Dialog, DialogContent, DialogTitle,
  CircularProgress, IconButton, Select, MenuItem, Card, Grid, Chip, Tooltip,
  Paper, Switch, FormControlLabel, Autocomplete, alpha
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Close as CloseIcon, ArrowUpward, ArrowDownward, Search as SearchIcon,
  CheckCircle as CheckCircleIcon, CloudUpload as CloudUploadIcon
} from '@mui/icons-material';
import api from '../services/api';
import { useSnackbar } from 'notistack';

const PRIMARY = '#4c5e8a';
const SEASONS = ['春季', '夏季', '秋季', '冬季', '四季'];
const OCCASIONS = ['休閒', '上班', '約會', '運動', '正式'];
const ROLES = ['top', 'bottom', 'outer', 'shoes', 'accessory', 'bag'];

interface ProductImage {
  src: string;
  alt?: string;
  position?: number;
}

interface ProductVariant {
  id: string;
  title: string;
  price: string;
  compare_at_price: string | null;
  sku: string;
  available: boolean;
  option1: string;
  option2: string;
  option3: string;
}

interface LookItemForm {
  product_id: string;
  role: string;
  position: number;
  variant_id: string;
  _title: string;
  _image: string;
  _images: ProductImage[];  // all images for this product
  _variants: ProductVariant[];
  _price: string;
  _handle: string;
}

interface LookForm {
  title: string;
  description: string;
  hero_image_url: string;
  season: string;
  occasion: string;
  status: 'draft' | 'active';
  sort_order: number;
  items: LookItemForm[];
}

interface SearchProduct {
  id: string;
  handle: string;
  title: string;
  product_type: string;
  image_src: string;
  images?: ProductImage[];
  variants?: ProductVariant[];
  price_range: { min: string; max: string };
}

interface Look {
  id: number;
  title: string;
  description: string;
  hero_image_url: string;
  season: string;
  occasion: string;
  status: string;
  sort_order: number;
  items: Array<{
    product_id: string;
    role: string;
    position: number;
    product?: { title: string; image_src?: string; images?: ProductImage[]; price_range?: { min: string }; handle: string };
  }>;
}

const emptyForm = (): LookForm => ({
  title: '', description: '', hero_image_url: '',
  season: '', occasion: '', status: 'draft', sort_order: 0, items: [],
});

/** Get unique color options from variants (option1 is typically color) */
function getColorOptions(variants: ProductVariant[]): string[] {
  const colors: string[] = [];
  variants.forEach(v => { if (v.option1 && !colors.includes(v.option1)) colors.push(v.option1); });
  return colors;
}

function inferRole(productType: string): string {
  const t = (productType || '').toLowerCase();
  if (/shirt|上衣|t-shirt|polo/i.test(t)) return 'top';
  if (/pants|褲/i.test(t)) return 'bottom';
  if (/jacket|外套/i.test(t)) return 'outer';
  if (/bag|包/i.test(t)) return 'bag';
  if (/shoe|鞋/i.test(t)) return 'shoes';
  return 'accessory';
}

const LooksAdminPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [looks, setLooks] = useState<Look[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<LookForm>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Look | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [searchOptions, setSearchOptions] = useState<SearchProduct[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [uploadedHeroImages, setUploadedHeroImages] = useState<string[]>([]);
  const [uploadingHero, setUploadingHero] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const fetchLooks = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/api/storefront/outfit-looks/admin/list');
      const list = Array.isArray(data) ? data : data.looks || [];
      list.sort((a: Look, b: Look) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
      setLooks(list);
    } catch {
      enqueueSnackbar('載入穿搭列表失敗', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => { fetchLooks(); }, [fetchLooks]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setUploadedHeroImages([]);
    setDialogOpen(true);
  };

  const openEdit = async (look: Look) => {
    setEditingId(look.id);
    const items = (look.items || [])
      .sort((a, b) => a.position - b.position)
      .map((it, i) => {
        const variants = (it.product as any)?.variants || [];
        return {
          product_id: String(it.product_id),
          role: it.role || 'accessory',
          position: i,
          // Default to first variant if not locked (matches addProduct behavior).
          // Closet matching requires variant_id when status='active'.
          variant_id: (it as any).variant_id || variants[0]?.id || '',
          _title: it.product?.title || `商品 ${it.product_id}`,
          _image: it.product?.images?.[0]?.src || it.product?.image_src || '',
          _images: it.product?.images?.length ? it.product.images : (it.product?.image_src ? [{ src: it.product.image_src }] : []),
          _variants: variants,
          _price: it.product?.price_range?.min || '',
          _handle: it.product?.handle || '',
        };
      });
    setForm({
      title: look.title || '',
      description: look.description || '',
      hero_image_url: look.hero_image_url || '',
      season: look.season || '',
      occasion: look.occasion || '',
      status: (look.status as 'draft' | 'active') || 'draft',
      sort_order: look.sort_order ?? 0,
      items,
    });
    // If existing hero isn't sourced from any item's product images, treat it as a custom upload
    const heroUrl = look.hero_image_url || '';
    const allItemImageSrcs = new Set(items.flatMap(it => it._images.map(img => img.src)));
    setUploadedHeroImages(heroUrl && !allItemImageSrcs.has(heroUrl) ? [heroUrl] : []);
    setDialogOpen(true);
  };

  const handleHeroFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-uploading same file
    if (!file) return;
    try {
      setUploadingHero(true);
      const fd = new FormData();
      fd.append('image', file);
      const { data } = await api.post('/api/storefront/outfit-looks/admin/upload-hero', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url: string = data?.url || '';
      if (!url) throw new Error('No URL returned');
      setUploadedHeroImages((prev) => (prev.includes(url) ? prev : [url, ...prev]));
      setForm((f) => ({ ...f, hero_image_url: url }));
      enqueueSnackbar('圖片已上傳', { variant: 'success' });
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.error || '圖片上傳失敗', { variant: 'error' });
    } finally {
      setUploadingHero(false);
    }
  };

  const removeUploadedHero = (url: string) => {
    setUploadedHeroImages((prev) => prev.filter((u) => u !== url));
    setForm((f) => (f.hero_image_url === url ? { ...f, hero_image_url: '' } : f));
  };

  const handleSearchChange = (_: unknown, value: string) => {
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!value || value.length < 2) { setSearchOptions([]); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        setSearchLoading(true);
        const { data } = await api.get('/api/storefront/outfit-looks/admin/search-products', {
          params: { q: value },
        });
        setSearchOptions(Array.isArray(data) ? data : data.products || []);
      } catch {
        setSearchOptions([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
  };

  const addProduct = (product: SearchProduct | null) => {
    if (!product) return;
    if (form.items.some((it) => it.product_id === String(product.id))) {
      enqueueSnackbar('此商品已在列表中', { variant: 'warning' });
      return;
    }
    setForm((f) => {
      const allImages = product.images?.length ? product.images : (product.image_src ? [{ src: product.image_src }] : []);
      const allVariants = product.variants || [];
      const newItem: LookItemForm = {
        product_id: String(product.id),
        role: inferRole(product.product_type),
        position: f.items.length,
        variant_id: allVariants[0]?.id || '',
        _title: product.title,
        _image: product.image_src,
        _images: allImages,
        _variants: allVariants,
        _price: product.price_range?.min || '',
        _handle: product.handle,
      };
      const newItems = [...f.items, newItem];
      return {
        ...f,
        items: newItems,
        // Auto-set hero image to first product's image if not yet set
        hero_image_url: f.hero_image_url || product.image_src || '',
        // Auto-generate title from product names if empty
        title: f.title || newItems.map(i => i._title.replace(/【.*?】/g, '').trim().split(' ')[0]).join(' × '),
      };
    });
    setSearchInput('');
    setSearchOptions([]);
  };

  const removeItem = (idx: number) => {
    setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  };

  const swapItems = (idx: number, dir: -1 | 1) => {
    const target = idx + dir;
    setForm((f) => {
      if (target < 0 || target >= f.items.length) return f;
      const next = [...f.items];
      [next[idx], next[target]] = [next[target], next[idx]];
      return { ...f, items: next };
    });
  };

  const setItemRole = (idx: number, role: string) => {
    setForm((f) => {
      const next = [...f.items];
      next[idx] = { ...next[idx], role };
      return { ...f, items: next };
    });
  };

  const setItemVariant = (idx: number, variant_id: string) => {
    setForm((f) => {
      const next = [...f.items];
      next[idx] = { ...next[idx], variant_id };
      return { ...f, items: next };
    });
  };

  const handleSave = async () => {
    if (form.items.length === 0) {
      enqueueSnackbar('請至少加入一件商品', { variant: 'warning' });
      return;
    }
    // Active looks must have every item's variant (color) locked so the
    // storefront virtual-closet matcher can resolve outfits. Drafts are exempt.
    if (form.status === 'active') {
      const missing = form.items.filter(it => !it.variant_id || !String(it.variant_id).trim());
      if (missing.length) {
        enqueueSnackbar(
          `上架穿搭需要每件商品鎖定變體（顏色）：還有 ${missing.length} 件未指定`,
          { variant: 'error' }
        );
        return;
      }
    }
    try {
      setSaving(true);
      const autoTitle = form.title.trim() || form.items.map(i => i._title.replace(/【.*?】/g, '').trim().split(' ')[0]).join(' × ');
      const payload = {
        title: autoTitle,
        description: form.description.trim(),
        hero_image_url: form.hero_image_url.trim(),
        season: form.season,
        occasion: form.occasion,
        status: form.status,
        sort_order: form.sort_order,
        items: form.items.map((it, i) => ({
          product_id: it.product_id,
          role: it.role,
          position: i,
          variant_id: it.variant_id || '',
        })),
      };
      if (editingId) {
        await api.put(`/api/storefront/outfit-looks/${editingId}`, payload);
      } else {
        await api.post('/api/storefront/outfit-looks', payload);
      }
      enqueueSnackbar('穿搭已儲存', { variant: 'success' });
      setDialogOpen(false);
      fetchLooks();
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.error || '儲存失敗', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await api.delete(`/api/storefront/outfit-looks/${deleteTarget.id}`);
      enqueueSnackbar('已刪除', { variant: 'success' });
      setDeleteTarget(null);
      fetchLooks();
    } catch {
      enqueueSnackbar('刪除失敗', { variant: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const totalPrice = (items: Look['items']) =>
    items.reduce((sum, it) => sum + Number(it.product?.price_range?.min || 0), 0);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1000, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>穿搭管理</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}
          sx={{ bgcolor: PRIMARY, '&:hover': { bgcolor: alpha(PRIMARY, 0.85) } }}>
          新增穿搭
        </Button>
      </Box>

      {/* Looks list */}
      {looks.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary', borderRadius: 2 }}>
          尚無穿搭，點擊右上角新增
        </Paper>
      ) : (
        looks.map((look) => (
          <Card key={look.id} sx={{
            display: 'flex', alignItems: 'center', mb: 1.5, border: '1px solid #e0e0e0',
            borderRadius: 2, boxShadow: 'none', p: 1.5,
          }}>
            <Box sx={{
              width: 80, height: 80, borderRadius: 1, overflow: 'hidden', flexShrink: 0,
              bgcolor: '#f5f5f5', mr: 2,
            }}>
              {look.hero_image_url ? (
                <img src={look.hero_image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#bbb', fontSize: 12 }}>
                  無圖片
                </Box>
              )}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography fontWeight={600} noWrap>{look.title || '(未命名)'}</Typography>
                <Chip size="small" label={look.status === 'active' ? '上架' : '草稿'}
                  sx={{
                    bgcolor: look.status === 'active' ? alpha('#4caf50', 0.12) : alpha('#ff9800', 0.12),
                    color: look.status === 'active' ? '#2e7d32' : '#e65100',
                    fontWeight: 600, fontSize: 12,
                  }} />
              </Box>
              <Typography variant="body2" color="text.secondary">
                {look.items?.length || 0} 件商品
                {look.items?.length ? ` · NT$${totalPrice(look.items).toLocaleString()}` : ''}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                {(look.season || '').split(',').filter(Boolean).map(s =>
                  <Chip key={`s-${s}`} size="small" label={s} variant="outlined" sx={{ fontSize: 11 }} />
                )}
                {(look.occasion || '').split(',').filter(Boolean).map(o =>
                  <Chip key={`o-${o}`} size="small" label={o} variant="outlined" sx={{ fontSize: 11 }} />
                )}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
              <Tooltip title="編輯">
                <IconButton size="small" onClick={() => openEdit(look)}><EditIcon fontSize="small" /></IconButton>
              </Tooltip>
              <Tooltip title="刪除">
                <IconButton size="small" color="error" onClick={() => setDeleteTarget(look)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Card>
        ))
      )}

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => !deleting && setDeleteTarget(null)}>
        <DialogContent sx={{ p: 3, minWidth: 300 }}>
          <Typography>確定要刪除「{deleteTarget?.title}」嗎？</Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
            <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>取消</Button>
            <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>
              {deleting ? <CircularProgress size={20} /> : '刪除'}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {editingId ? '編輯穿搭' : '新增穿搭'}
          <IconButton onClick={() => setDialogOpen(false)} disabled={saving}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          <Grid container spacing={2}>
            {/* Description stays hidden — the /looks-page-outfits script stores a
                sentinel there ("[looks-skill look_N]") and re-finds looks by it. */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="標題"
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="留空則自動以商品名稱組合"
                helperText="腳本產生的預設格式是「上身色 ✕ 下身色」；前台會在 ✕ 上斷行排版。改標題不會更新 /looks 顏色篩選（derived_colors 另存）。"
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  主圖
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={uploadingHero ? <CircularProgress size={14} /> : <CloudUploadIcon sx={{ fontSize: 16 }} />}
                  disabled={uploadingHero}
                  onClick={() => fileInputRef.current?.click()}
                  sx={{ textTransform: 'none', fontSize: 13 }}>
                  {uploadingHero ? '上傳中...' : '上傳圖片'}
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  hidden
                  onChange={handleHeroFileChange}
                />
              </Box>
              {(uploadedHeroImages.length > 0 || form.items.length > 0) ? (
                <Box sx={{ maxHeight: 260, overflowY: 'auto', border: '1px solid #eee', borderRadius: 1, p: 1.5 }}>
                  {uploadedHeroImages.length > 0 && (
                    <Box sx={{ mb: 1.5, pb: 1.5, borderBottom: form.items.length > 0 ? '1px dashed #e0e0e0' : 'none' }}>
                      <Typography variant="caption" sx={{ color: '#888', mb: 0.5, display: 'block', fontWeight: 500 }}>
                        已上傳的圖片
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                        {uploadedHeroImages.map((url) => (
                          <Box key={url} sx={{ position: 'relative' }}>
                            <Box
                              onClick={() => setForm((f) => ({ ...f, hero_image_url: url }))}
                              sx={{
                                width: 64, height: 85, borderRadius: 0.75, overflow: 'hidden', cursor: 'pointer',
                                border: form.hero_image_url === url ? `2.5px solid ${PRIMARY}` : '1.5px solid #e0e0e0',
                                position: 'relative', transition: 'border-color 0.15s',
                                '&:hover': { borderColor: PRIMARY, opacity: 0.9 },
                              }}>
                              <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                              {form.hero_image_url === url && (
                                <CheckCircleIcon sx={{
                                  position: 'absolute', top: 1, right: 1, fontSize: 16,
                                  color: PRIMARY, bgcolor: '#fff', borderRadius: '50%',
                                }} />
                              )}
                            </Box>
                            <Tooltip title="從清單移除">
                              <IconButton
                                size="small"
                                onClick={() => removeUploadedHero(url)}
                                sx={{
                                  position: 'absolute', top: -8, left: -8, p: 0.25,
                                  bgcolor: '#fff', border: '1px solid #ddd',
                                  '&:hover': { bgcolor: '#fee' },
                                }}>
                                <CloseIcon sx={{ fontSize: 12 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}
                  {form.items.filter(it => it._images?.length > 0).map((it) => (
                    <Box key={it.product_id} sx={{ mb: 1.5, '&:last-child': { mb: 0 } }}>
                      <Typography variant="caption" sx={{ color: '#888', mb: 0.5, display: 'block', fontWeight: 500 }}>
                        {it._title}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                        {it._images.map((img, imgIdx) => (
                          <Tooltip key={`${it.product_id}-${imgIdx}`} title={`${it._title} #${imgIdx + 1}`}>
                            <Box
                              onClick={() => setForm(f => ({ ...f, hero_image_url: img.src }))}
                              sx={{
                                width: 64, height: 85, borderRadius: 0.75, overflow: 'hidden', cursor: 'pointer',
                                border: form.hero_image_url === img.src ? `2.5px solid ${PRIMARY}` : '1.5px solid #e0e0e0',
                                position: 'relative', transition: 'border-color 0.15s',
                                '&:hover': { borderColor: PRIMARY, opacity: 0.9 },
                              }}>
                              <img src={img.src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                              {form.hero_image_url === img.src && (
                                <CheckCircleIcon sx={{
                                  position: 'absolute', top: 1, right: 1, fontSize: 16,
                                  color: PRIMARY, bgcolor: '#fff', borderRadius: '50%',
                                }} />
                              )}
                            </Box>
                          </Tooltip>
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box sx={{ p: 2, bgcolor: '#f9f9f9', borderRadius: 1, textAlign: 'center', color: '#aaa', fontSize: 13 }}>
                  上傳圖片，或加入商品後從商品圖片中選擇主圖
                </Box>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth select label="季節 (可複選)"
                value={form.season ? form.season.split(',').filter(Boolean) : []}
                onChange={(e) => {
                  const val = e.target.value as unknown as string[];
                  setForm((f) => ({ ...f, season: Array.isArray(val) ? val.join(',') : val }));
                }}
                SelectProps={{
                  multiple: true,
                  renderValue: (selected) => (selected as string[]).join('、') || '(無)',
                }}>
                {SEASONS.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth select label="場合 (可複選)"
                value={form.occasion ? form.occasion.split(',').filter(Boolean) : []}
                onChange={(e) => {
                  const val = e.target.value as unknown as string[];
                  setForm((f) => ({ ...f, occasion: Array.isArray(val) ? val.join(',') : val }));
                }}
                SelectProps={{
                  multiple: true,
                  renderValue: (selected) => (selected as string[]).join('、') || '(無)',
                }}>
                {OCCASIONS.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}>
              <FormControlLabel label="上架" control={
                <Switch checked={form.status === 'active'}
                  onChange={(e) => setForm((f) => ({ ...f, status: e.target.checked ? 'active' : 'draft' }))} />
              } />
              {form.status === 'active' && (() => {
                const missing = form.items.filter(it => !it.variant_id || !String(it.variant_id).trim()).length;
                return missing > 0 ? (
                  <Typography variant="caption" sx={{ color: '#d32f2f', display: 'block', mt: 0.5 }}>
                    {missing} 件未指定變體（衣櫥配對需要）
                  </Typography>
                ) : null;
              })()}
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth label="排序" type="number" value={form.sort_order}
                onChange={(e) => setForm((f) => ({ ...f, sort_order: Number(e.target.value) || 0 }))} />
            </Grid>
          </Grid>

          {/* Product search */}
          <Typography variant="subtitle2" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>商品搜尋</Typography>
          <Autocomplete
            freeSolo
            options={searchOptions}
            loading={searchLoading}
            inputValue={searchInput}
            onInputChange={handleSearchChange}
            onChange={(_, val) => addProduct(val as SearchProduct | null)}
            getOptionLabel={(opt) => typeof opt === 'string' ? opt : opt.title}
            filterOptions={(x) => x}
            renderOption={(props, opt) => (
              <Box component="li" {...props} key={opt.id} sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                <img src={opt.image_src} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }}
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                <Box>
                  <Typography variant="body2" noWrap>{opt.title}</Typography>
                  <Typography variant="caption" color="text.secondary">{opt.product_type}</Typography>
                </Box>
              </Box>
            )}
            renderInput={(params) => (
              <TextField {...params} placeholder="搜尋商品名稱..." size="small"
                slotProps={{ input: { ...params.InputProps, startAdornment: <SearchIcon sx={{ color: '#aaa', mr: 0.5 }} /> } }} />
            )}
          />

          {/* Items list */}
          <Typography variant="subtitle2" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
            已選商品 ({form.items.length})
          </Typography>
          {form.items.length === 0 ? (
            <Paper sx={{ p: 2, textAlign: 'center', color: 'text.secondary', fontSize: 14, borderRadius: 1 }}>
              搜尋並選擇商品加入穿搭
            </Paper>
          ) : (
            form.items.map((it, idx) => (
              <Paper key={it.product_id} variant="outlined" sx={{
                display: 'flex', alignItems: 'center', gap: 1.5, p: 1, mb: 1, borderRadius: 1,
              }}>
                <Typography sx={{ width: 24, textAlign: 'center', color: 'text.secondary', fontSize: 13, flexShrink: 0 }}>
                  {idx + 1}
                </Typography>
                <Box sx={{ width: 48, height: 48, borderRadius: 0.5, overflow: 'hidden', flexShrink: 0, bgcolor: '#f5f5f5' }}>
                  {it._image && (
                    <img src={it._image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                  )}
                </Box>
                <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>{it._title}</Typography>
                <Select size="small" value={it.role} onChange={(e) => setItemRole(idx, e.target.value as string)}
                  sx={{ minWidth: 80, fontSize: 13 }}>
                  {ROLES.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                </Select>
                {it._variants.length > 1 && (
                  <Select size="small" value={it.variant_id || it._variants[0]?.id || ''}
                    onChange={(e) => setItemVariant(idx, e.target.value as string)}
                    sx={{ minWidth: 120, fontSize: 13 }}>
                    {it._variants.map((v) => (
                      <MenuItem key={v.id} value={v.id} sx={{ fontSize: 13 }}>
                        {v.option1}{v.option2 ? ` / ${v.option2}` : ''}
                        {v.option3 ? ` / ${v.option3}` : ''}
                        {` · $${v.price}`}
                      </MenuItem>
                    ))}
                  </Select>
                )}
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <IconButton size="small" disabled={idx === 0} onClick={() => swapItems(idx, -1)}>
                    <ArrowUpward sx={{ fontSize: 16 }} />
                  </IconButton>
                  <IconButton size="small" disabled={idx === form.items.length - 1} onClick={() => swapItems(idx, 1)}>
                    <ArrowDownward sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
                <IconButton size="small" color="error" onClick={() => removeItem(idx)}>
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Paper>
            ))
          )}

          {/* Action buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
            <Button onClick={() => setDialogOpen(false)} disabled={saving}>取消</Button>
            <Button variant="contained" onClick={handleSave} disabled={saving}
              sx={{ bgcolor: PRIMARY, '&:hover': { bgcolor: alpha(PRIMARY, 0.85) } }}>
              {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : '儲存'}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default LooksAdminPage;
